import Alert from '@mui/material/Alert'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useUsersView } from 'context/UsersViewContext'
import useDialog from 'global/hooks/useDialog'
import { useEffect, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import KNLoader from 'components/KNLoader/KNLoader'
import UserFilters from './UserFilters'
import UserTable from './UserTable'
import CreateOrEditUserDialog from './CreateOrEditUserDialog'
import BulkCreateUsersDialog from './BulkCreateUsersDialog'
import { analyticsPageView } from 'global/helpers/analytics'
import { isCompanyType, hasAccess } from 'global/helpers/authorization'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { getFilteredUsers, USERS_QUERY_KEY } from './UserManager.service'
import { CompanyType } from 'context/authentication/Company.types'
import { Role } from 'context/authentication/Role.types'

const UserManager = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, page } = useUsersView()
  const usersQuery = useQuery({
    queryKey: [USERS_QUERY_KEY, { page, filters }],
    queryFn: () => getFilteredUsers(page, filters),
    placeholderData: keepPreviousData,
  })
  const createOrEditUserDialog = useDialog<typeof CreateOrEditUserDialog>()
  const bulkCreateUsersDialog = useDialog<typeof BulkCreateUsersDialog>()

  useEffect(() => {
    if (!isCompanyType(CompanyType.Operator)) {
      analyticsPageView('polestar/users_list')
    }
  }, [])

  return (
    <Container maxWidth="xxl">
      {usersQuery.isPending && (
        <KNLoader>
          <Typography>{t('screens.shared.user_manager.loading')}</Typography>
        </KNLoader>
      )}
      {usersQuery.isError && (
        <Alert severity="error">{getRequestErrorMessage(usersQuery.error)}</Alert>
      )}
      {usersQuery.isSuccess && (
        <>
          <Paper sx={{ padding: 2 }}>
            <Stack direction="column" spacing={1} mb={1}>
              <UserFilters />
              <Stack
                spacing={1}
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'start', md: 'center' }}
                mb={2}
              >
                <Typography variant="h6">
                  {t('screens.shared.user_manager.users_count', { count: usersQuery.data?.totalElements })}
                </Typography>
                {hasAccess(Role.Admin) && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={createOrEditUserDialog.handleOpen}
                      variant="contained"
                      color="secondary"
                      size="small"
                    >
                      {t('screens.shared.user_manager.card.actions.new')}
                    </Button>
                    {isCompanyType(CompanyType.Operator) && (
                      <Button
                        onClick={bulkCreateUsersDialog.handleOpen}
                        variant="contained"
                        color="secondary"
                        size="small"
                      >
                        {t('screens.shared.user_manager.card.actions.bulk')}
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>
            <UserTable
              users={usersQuery.data?.content ?? []}
              pageCount={usersQuery.data?.totalPages}
            />
          </Paper>
          {hasAccess(Role.Admin) && (
            <>
              <CreateOrEditUserDialog
                open={createOrEditUserDialog.isOpen}
                onAction={createOrEditUserDialog.handleAction}
                onClose={createOrEditUserDialog.handleClose}
              />
              {isCompanyType(CompanyType.Operator) && (
                <BulkCreateUsersDialog
                  open={bulkCreateUsersDialog.isOpen}
                  onAction={bulkCreateUsersDialog.handleAction}
                  onClose={bulkCreateUsersDialog.handleClose}
                />
              )}
            </>
          )}
        </>
      )}
    </Container>
  )
}

export default UserManager
